'use strict';

const Measurement = require('bunsen/models/inventory/Measurement');
const displayConstants = require('../constants/measurementConstants').display;
const numberConversions = require('dibs-measurement-conversions');

module.exports = {
    /**
     * @param {string|number} value The measurement value
     * @param {string} units The measurement unit
     * @returns string
     */
    addUnits(value, units) {
        return `${value}${units}`;
    },

    /**
     * @param {string|number} value The measurement value
     * @param {string} units The measurement unit
     * @returns string
     */
    addUnitsInParenthesis(value, units) {
        return `${value} (${units})`;
    },

    /**
     * @param {string|number} centimeters The measurement in centimeters to convert to inches
     * @returns number
     */
    centimetersToInches({ value, feetCutOff = numberConversions.DEFAULT_FEET_CUTOFF }) {
        const centimetersNum = parseFloat(value);
        let inches;

        if (centimetersNum) {
            inches = numberConversions.convert({
                num: centimetersNum,
                from: 'cm',
                to: 'inch',
                feetCutOff,
            });
        }

        return inches;
    },

    /**
     * @param {string|number} inches The measurement in inches to convert to centimeters
     * @param {boolean} round Round the result? Defaults to `true` per existing logic
     * @returns number
     */
    inchesToCentimeters({
        value,
        round = true,
        feetCutOff = numberConversions.DEFAULT_FEET_CUTOFF,
    }) {
        const inchesNum = parseFloat(value);
        let centimeters;

        if (inchesNum) {
            centimeters = numberConversions.convert({
                num: inchesNum,
                from: 'inch',
                to: 'cm',
                feetCutOff,
            });
        }

        return round ? Math.round(centimeters) : centimeters;
    },

    /**
     * Creates a formatted string for inches in `longForm` format
     * @param {number|string} inches
     * @returns {string}
     */
    formatInchesNoFeet({ value, feetCutOff = numberConversions.DEFAULT_FEET_CUTOFF }) {
        // convert is expecting either ' or "
        value += '"';

        return numberConversions.convert({ num: value, from: 'feet', to: 'longForm', feetCutOff });
    },

    /**
     * Creates a formatted string for inches in `longForm` format
     * @param {number|string} inches
     * @returns {string}
     */
    formatInchesAndFeet({ value, feetCutOff = numberConversions.DEFAULT_FEET_CUTOFF }) {
        const feet = numberConversions.convert({
            num: value,
            from: 'inch',
            to: 'feet',
            feetCutOff,
        });
        return numberConversions.convert({
            num: feet,
            from: 'feet',
            to: 'longForm',
            feetCutOff,
        });
    },

    /**
     * Creates a formatted string of dimensions in "# in. (# cm)" format or "# mm" format
     * question: should this formatting round by default?
     * @param {string|number} measurement The measurement value
     * @param {string} units The measurement unit
     * @param {bool} rugOrMirror Is item a rug or mirror
     * @returns {string}
     */
    formatMeasurement(measurement, units, rugOrMirror = false, isFurniture = false) {
        let formattedMeasurement;

        switch (units) {
            case Measurement.units.MM:
                formattedMeasurement = `${measurement} ${displayConstants[Measurement.units.MM]}`;
                break;
            case Measurement.units.CM:
            case Measurement.units.IN:
                let centimeters;
                let inches;
                let formattedInches;
                const feetCutoff = rugOrMirror
                    ? numberConversions.LOWER_FEET_CUTOFF
                    : numberConversions.DEFAULT_FEET_CUTOFF;
                // reassigning all of these vars for clarity
                if (units === Measurement.units.CM) {
                    centimeters = measurement;
                    inches = this.centimetersToInches({ value: measurement });
                    formattedInches = isFurniture
                        ? this.formatInchesAndFeet({ value: inches, feetCutOff: feetCutoff })
                        : this.formatInchesNoFeet({ value: inches, feetCutOff: feetCutoff });
                } else {
                    centimeters = this.inchesToCentimeters({ value: measurement });
                    inches = measurement;
                    formattedInches = isFurniture
                        ? this.formatInchesAndFeet({ value: inches, feetCutOff: feetCutoff })
                        : this.formatInchesNoFeet({ value: inches, feetCutOff: feetCutoff });
                }
                // old logic: always put `cm` in parens
                formattedMeasurement = `${formattedInches} (${centimeters} ${
                    displayConstants[Measurement.units.CM]
                })`;
                break;
        }

        return formattedMeasurement;
    },
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/helpers/measurementFormatters.es.js